<script setup lang="ts">
    import { onClickOutside } from '@vueuse/core';

    const { $theme } = useNuxtApp();
    const emit = defineEmits(['shown', 'hidden']);

    interface Props {
        container?: string;
        alignment?: 'top-full' | 'top-0' | 'bottom-0' | 'bottom-full';
        position?: 'left-full' | 'left-0' | 'right-0' | 'right-full' | 'center-x' | 'screen-center';
        overlay?: boolean;
        maxHeight?: string;
        withoutMaxHeight?: boolean;
        relativeDivClass?: string;
        /**
         * Drop the full-screen backdrop and dismiss on an outside click instead. The backdrop sits at
         * `inset-0`, so it swallows wheel and touch events aimed at whatever is behind it — fine for a
         * menu you pick from and leave, wrong for one that stays open beside a scrollable list (the
         * Messages filter menu, TDD-3724, where the design keeps it open to chain both filter groups).
         */
        withoutOverlay?: boolean;
    }
    const props = defineProps<Props>();

    // Centers the menu in the viewport instead of anchoring it to the trigger.
    // Useful on mobile where an anchored menu (e.g. user select in a table) would
    // overflow off-screen (TDD-3412).
    const isScreenCentered = computed(() => props.position === 'screen-center');

    const submenu = ref(null);
    const submenuContainer = ref(null);
    const isDisplayed = ref(false);
    const submenuClass = computed(() => {
        let maxHeight = '';
        if (!props.withoutMaxHeight) {
            maxHeight = `${props.maxHeight || 'max-h-[300px]'} overflow-y-auto nice-scrollbar `;
        }

        const container = props.container ? props.container : $theme('submenu.container.large');

        if (isScreenCentered.value) {
            // Menu sizing only; the fixed/flex wrapper handles viewport centering so the
            // transition transforms (translate/scale) don't fight the positioning.
            return `${container} relative z-[120] ${maxHeight}`;
        }

        return `${container} ${props.position || 'left-0'} ${maxHeight} ${props.alignment || ' top-full'}`;
    });

    const show = () => {
        if (isDisplayed.value) {
            return;
        }

        isDisplayed.value = true;
        emit('shown');

        nextTick(() => {
            submenuContainer.value?.focus();
        });
    };

    const hide = () => {
        if (!isDisplayed.value) {
            return;
        }

        isDisplayed.value = false;
        emit('hidden');
    };

    const toggle = () => {
        isDisplayed.value ? hide() : show();
    };

    // Without the backdrop there is nothing to catch the click that should dismiss the menu, so watch
    // for clicks landing outside the trigger + panel instead. `hide()` already no-ops when closed.
    onClickOutside(submenu, () => {
        if (props.withoutOverlay) hide();
    });

    defineExpose({
        isDisplayed,
        show,
        hide,
        toggle,
    });
</script>

<template>
    <div
        ref="submenu"
        class="relative"
        :class="relativeDivClass ?? ''"
    >
        <slot
            :hide="hide"
            :show="show"
            :toggle="toggle"
            :is-displayed="isDisplayed"
        ></slot>
        <!-- Wrapper centers the menu in the viewport when position is 'screen-center'. -->
        <div
            v-if="isScreenCentered"
            class="fixed inset-0 z-[120] flex items-center justify-center pointer-events-none p-4"
        >
            <transition
                :enter-active-class="$theme('submenu.transition.enter-active-class')"
                :enter-from-class="$theme('submenu.transition.enter-from-class')"
                :enter-to-class="$theme('submenu.transition.enter-to-class')"
                :leave-active-class="$theme('submenu.transition.leave-active-class')"
                :leave-from-class="$theme('submenu.transition.leave-from-class')"
                :leave-to-class="$theme('submenu.transition.leave-to-class')"
            >
                <div
                    v-if="isDisplayed"
                    ref="submenuContainer"
                    :class="`${submenuClass} pointer-events-auto`"
                    :tabindex="isDisplayed ? 100 : -1"
                    @keydown.esc.stop="hide()"
                >
                    <slot
                        name="submenu"
                        :hide="hide"
                    ></slot>
                </div>
            </transition>
        </div>
        <transition
            v-else
            :enter-active-class="$theme('submenu.transition.enter-active-class')"
            :enter-from-class="$theme('submenu.transition.enter-from-class')"
            :enter-to-class="$theme('submenu.transition.enter-to-class')"
            :leave-active-class="$theme('submenu.transition.leave-active-class')"
            :leave-from-class="$theme('submenu.transition.leave-from-class')"
            :leave-to-class="$theme('submenu.transition.leave-to-class')"
        >
            <div
                v-if="isDisplayed"
                ref="submenuContainer"
                :class="`${submenuClass} mt-1`"
                :tabindex="isDisplayed ? 100 : -1"
                @keydown.esc.stop="hide()"
            >
                <slot
                    name="submenu"
                    :hide="hide"
                ></slot>
            </div>
        </transition>
        <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-all duration-200 ease-out delay-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isDisplayed && !withoutOverlay"
                :class="[$theme('submenu.overlay.base'), overlay || isScreenCentered ? 'bg-[#0a183a45] backdrop-blur-[1px]' : '']"
                @click.stop.prevent="hide()"
                @touchmove.prevent
                data-role="submenu-overlay"
            ></div>
        </transition>
    </div>
</template>
